<script setup lang="ts">
/**
 * The in-app banner (src/native/inAppBanner.ts): a glass card that drops in under the status bar when a push
 * arrives while the app is open, like iOS's own banner but inside the app, so tapping it routes in place.
 *
 * Tap opens (the entry's `onOpen`), a flick up dismisses, it leaves by itself after a few seconds (paused while
 * a finger is on it). Thicker glass than the chat chrome: it sits over arbitrary content and must stay legible.
 */
import { onBeforeUnmount, ref, watch } from 'vue'
import logoWhite from '@/assets/images/LogoWhite256.png'
import { dismissInAppBanner, IN_APP_BANNER_MS, useInAppBanner, type InAppBannerEntry } from './inAppBanner'
import { useTopIsland } from './topIsland'

const { current } = useInAppBanner()
const shown = ref<InAppBannerEntry | null>(null)
const dragY = ref(0)
const dragging = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
let startY = 0
let startAt = 0
let moved = false

function arm(entry: InAppBannerEntry) {
    clearTimeout(timer)
    timer = setTimeout(() => dismissInAppBanner(entry.id), entry.durationMs ?? IN_APP_BANNER_MS)
}

watch(current, (entry) => {
    clearTimeout(timer)
    dragY.value = 0
    shown.value = entry
    if (entry) arm(entry)
})

onBeforeUnmount(() => clearTimeout(timer))

/** Drops in under the call pill and the offline pill when they show (the top island). */
const islandOffset = useTopIsland('banner')

function onPointerDown(e: PointerEvent) {
    if (!shown.value) return
    clearTimeout(timer)
    dragging.value = true
    moved = false
    startY = e.clientY
    startAt = performance.now()
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
    if (!dragging.value) return
    const dy = e.clientY - startY
    if (Math.abs(dy) > 6) moved = true
    // Up follows the finger; down rubber-bands (there is nowhere to pull it to).
    dragY.value = dy < 0 ? dy : Math.sqrt(dy) * 3
}

function onPointerUp() {
    if (!dragging.value || !shown.value) return
    dragging.value = false
    const entry = shown.value
    const elapsed = Math.max(1, performance.now() - startAt)
    const velocity = dragY.value / elapsed // px/ms, negative = up
    if (dragY.value < -36 || velocity < -0.45) {
        dismissInAppBanner(entry.id)
        return
    }
    dragY.value = 0
    if (!moved) {
        dismissInAppBanner(entry.id)
        entry.onOpen?.()
        return
    }
    arm(entry)
}

function onPointerCancel() {
    if (!dragging.value || !shown.value) return
    dragging.value = false
    dragY.value = 0
    arm(shown.value)
}

function onKey(e: KeyboardEvent) {
    if (!shown.value) return
    if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        const entry = shown.value
        dismissInAppBanner(entry.id)
        entry.onOpen?.()
    }
}
</script>

<template>
    <Teleport to="body">
        <div class="iab-layer" :style="islandOffset ? { transform: `translate3d(0, ${islandOffset}px, 0)` } : undefined" aria-live="polite">
            <Transition name="iab">
                <div
                    v-if="shown"
                    :key="shown.id"
                    class="iab"
                    :class="{ 'is-dragging': dragging }"
                    :style="dragY ? { transform: `translate3d(0, ${dragY}px, 0)` } : undefined"
                    role="button"
                    tabindex="0"
                    :aria-label="[shown.title, shown.subtitle, shown.body].filter(Boolean).join('. ')"
                    @pointerdown="onPointerDown"
                    @pointermove="onPointerMove"
                    @pointerup="onPointerUp"
                    @pointercancel="onPointerCancel"
                    @keydown="onKey"
                >
                    <span class="iab-icon" aria-hidden="true">
                        <img :src="logoWhite" alt="" width="26" height="26" draggable="false">
                    </span>
                    <span class="iab-text">
                        <span class="iab-row">
                            <span class="iab-title">{{ shown.title }}</span>
                            <span class="iab-time">now</span>
                        </span>
                        <span v-if="shown.subtitle" class="iab-subtitle" :class="{ 'is-attention': shown.tone === 'attention' }">{{ shown.subtitle }}</span>
                        <span v-if="shown.body" class="iab-body">{{ shown.body }}</span>
                    </span>
                </div>
            </Transition>
        </div>
    </Teleport>
</template>

<style scoped>
.iab-layer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: var(--z-toast, 10080);
    pointer-events: none;
    padding: calc(env(safe-area-inset-top, 0px) + 8px) 8px 0;
    transition: transform var(--lh-dur-base, 240ms) var(--lh-ease-ios, cubic-bezier(0.32, 0.72, 0, 1));
    display: flex;
    justify-content: center;
}

.iab {
    pointer-events: auto;
    width: 100%;
    max-width: 440px;
    display: flex;
    align-items: flex-start;
    gap: 11px;
    padding: 11px 14px 12px 11px;
    border-radius: 22px;
    background: rgba(250, 250, 250, 0.78);
    -webkit-backdrop-filter: blur(24px) saturate(1.8);
    backdrop-filter: blur(24px) saturate(1.8);
    box-shadow:
        0 0 0 0.5px rgba(36, 36, 36, 0.08),
        0 12px 32px rgba(36, 36, 36, 0.16),
        0 2px 6px rgba(36, 36, 36, 0.06),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.6);
    color: var(--lh-text-1, #242424);
    text-align: left;
    cursor: pointer;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    transition: transform var(--lh-dur-pop, 280ms) var(--lh-spring-soft, cubic-bezier(0.34, 1.3, 0.64, 1));
}
.iab.is-dragging {
    transition: none;
}
.iab:focus-visible {
    outline: 2px solid var(--lh-accent, #fb4c1f);
    outline-offset: 2px;
}
html.dark .iab {
    background: rgba(41, 37, 36, 0.78);
    box-shadow:
        0 0 0 0.5px rgba(255, 255, 255, 0.1),
        0 14px 34px rgba(0, 0, 0, 0.5),
        0 2px 6px rgba(0, 0, 0, 0.3),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.06);
}

.iab-icon {
    flex: none;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: linear-gradient(160deg, #ff6a3d 0%, var(--lh-accent, #fb4c1f) 100%);
    box-shadow: inset 0 0 0 0.5px rgba(255, 255, 255, 0.25);
}
.iab-icon img {
    width: 26px;
    height: 26px;
    max-width: 26px;
    max-height: 26px;
    display: block;
}

.iab-text {
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding-top: 1px;
}
.iab-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
}
.iab-title {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.iab-time {
    flex: none;
    font-size: 12px;
    color: var(--lh-text-3, #737373);
}
.iab-subtitle {
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
    color: var(--lh-text-2, #525252);
}
.iab-subtitle.is-attention {
    color: var(--lh-accent, #fb4c1f);
}
.iab-body {
    font-size: 14px;
    line-height: 19px;
    color: var(--lh-text-2, #525252);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Drops in from above the status bar; leaves the same way, a little quicker. */
.iab-enter-active {
    transition:
        transform var(--lh-dur-settle, 480ms) var(--lh-ease-ios, cubic-bezier(0.32, 0.72, 0, 1)),
        opacity var(--lh-dur-fast, 150ms) ease;
}
.iab-leave-active {
    transition:
        transform var(--lh-dur-pop, 280ms) var(--lh-ease-in, cubic-bezier(0.4, 0, 1, 1)),
        opacity var(--lh-dur-pop, 280ms) ease;
}
.iab-enter-from,
.iab-leave-to {
    transform: translate3d(0, calc(-100% - 24px - env(safe-area-inset-top, 0px)), 0) !important;
    opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
    .iab-enter-from,
    .iab-leave-to {
        transform: none !important;
    }
}
</style>
